import React, { useState } from 'react';
import { ArrowLeft, Send, CheckCircle2, AlertCircle, HelpCircle, Info, FileText } from 'lucide-react';
import { Link } from 'react-router-dom';
import { IMAGES } from '../data';

export default function Moving() {
  const [formData, setFormData] = useState({
    adresse: '',
    postnrBy: '9310 Vodskov',
    fraflytterNavn: '',
    fraflytterNyAdresse: '',
    fraflytterTlf: '',
    fraflytterEmail: '',
    tilflytterNavn: '',
    tilflytterTlf: '',
    tilflytterEmail: '',
    overdragelsesDato: '',
    maelerstand: '',
    bemaerkninger: '',
    website_url: '', // Honeypot field for anti-spam
  });

  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMessage('');

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setStatus('success');
        // Reset form
        setFormData({
          adresse: '',
          postnrBy: '9310 Vodskov',
          fraflytterNavn: '',
          fraflytterNyAdresse: '',
          fraflytterTlf: '',
          fraflytterEmail: '',
          tilflytterNavn: '',
          tilflytterTlf: '',
          tilflytterEmail: '',
          overdragelsesDato: '',
          maelerstand: '',
          bemaerkninger: '',
          website_url: '',
        });
      } else {
        throw new Error('Der skete en fejl ved indsendelse af formularen. Prøv venligst igen.');
      }
    } catch (err) {
      console.error('Form submission error:', err);
      setStatus('error');
      setErrorMessage(err.message || 'Kunne ikke indsende formularen. Kontroller din internetforbindelse.');
    }
  };

  return (
    <div className="bg-slate-50 min-h-screen py-12 sm:py-16">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        
        {/* Breadcrumb */}
        <div className="mb-6">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-900 hover:text-slate-600 transition">
            <ArrowLeft className="h-4 w-4" />
            Tilbage til forsiden
          </Link>
        </div>

        {/* Header */}
        <div className="border-b border-slate-200 pb-8 mb-10">
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
            Flyttemeddelelse
          </h1>
          <p className="mt-3 text-lg text-slate-500">
            Skal du flytte? Meddel ejerskifte og flytning til Grindsted Vandværk ved at udfylde formularen herunder.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Form Column */}
          <div className="lg:col-span-8">
            {status === 'success' ? (
              <div className="rounded-3xl bg-emerald-50 border border-emerald-200 p-8 text-center shadow-sm">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
                  <CheckCircle2 className="h-8 w-8" />
                </div>
                <h3 className="mt-4 text-xl font-extrabold text-slate-900">Flyttemeddelelse modtaget!</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                  Mange tak for din indberetning. Vi har registreret flytningen og vil behandle oplysningerne hurtigst muligt. Der vil blive fremsendt en flytteopgørelse til fraflytter.
                </p>
                <div className="mt-6">
                  <button
                    onClick={() => setStatus('idle')}
                    className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-600 px-5 py-2.5 text-xs font-bold text-white hover:bg-emerald-500 transition shadow-sm"
                  >
                    Indsend endnu en flytning
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="bg-white rounded-3xl border border-slate-200 p-6 sm:p-8 shadow-sm space-y-8">
                
                {/* Honeypot field (ABSOLUTELY HIDDEN from humans) */}
                <div className="sr-only absolute -top-96 -left-96">
                  <label htmlFor="website_url">Efterlad dette felt tomt</label>
                  <input
                    type="text"
                    id="website_url"
                    name="website_url"
                    value={formData.website_url}
                    onChange={handleChange}
                    autoComplete="off"
                  />
                </div>

                {/* Section 1: Aflæsningssted */}
                <div className="space-y-4">
                  <h3 className="text-lg font-bold text-slate-900 border-b border-slate-100 pb-2">1. Aflæsningssted (Ejendommen)</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="sm:col-span-2">
                      <label htmlFor="adresse" className="block text-xs font-bold text-slate-700 uppercase tracking-wider">Adresse *</label>
                      <input
                        type="text"
                        id="adresse"
                        name="adresse"
                        required
                        value={formData.adresse}
                        onChange={handleChange}
                        placeholder="F.eks. Smedevej 4"
                        className="mt-1.5 w-full rounded-xl border border-slate-200 bg-slate-50/50 px-4 py-3 text-sm font-medium text-slate-900 focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:outline-none transition-all"
                      />
                    </div>
                    <div>
                      <label htmlFor="postnrBy" className="block text-xs font-bold text-slate-700 uppercase tracking-wider">Postnr. & By *</label>
                      <input
                        type="text"
                        id="postnrBy"
                        name="postnrBy"
                        required
                        value={formData.postnrBy}
                        onChange={handleChange}
                        className="mt-1.5 w-full rounded-xl border border-slate-200 bg-slate-50/50 px-4 py-3 text-sm font-medium text-slate-900 focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:outline-none transition-all"
                      />
                    </div>
                  </div>
                </div>

                {/* Section 2: Fraflytter */}
                <div className="space-y-4">
                  <h3 className="text-lg font-bold text-slate-900 border-b border-slate-100 pb-2">2. Fraflytter (Sælger / Udlejer)</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="sm:col-span-2">
                      <label htmlFor="fraflytterNavn" className="block text-xs font-bold text-slate-700 uppercase tracking-wider">Navn *</label>
                      <input
                        type="text"
                        id="fraflytterNavn"
                        name="fraflytterNavn"
                        required
                        value={formData.fraflytterNavn}
                        onChange={handleChange}
                        placeholder="Fraflytterens fulde navn"
                        className="mt-1.5 w-full rounded-xl border border-slate-200 bg-slate-50/50 px-4 py-3 text-sm font-medium text-slate-900 focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:outline-none transition-all"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label htmlFor="fraflytterNyAdresse" className="block text-xs font-bold text-slate-700 uppercase tracking-wider">Ny adresse (til slutfaktura) *</label>
                      <input
                        type="text"
                        id="fraflytterNyAdresse"
                        name="fraflytterNyAdresse"
                        required
                        value={formData.fraflytterNyAdresse}
                        onChange={handleChange}
                        placeholder="Ny gade, husnr., postnr. og by"
                        className="mt-1.5 w-full rounded-xl border border-slate-200 bg-slate-50/50 px-4 py-3 text-sm font-medium text-slate-900 focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:outline-none transition-all"
                      />
                    </div>
                    <div>
                      <label htmlFor="fraflytterTlf" className="block text-xs font-bold text-slate-700 uppercase tracking-wider">Telefon *</label>
                      <input
                        type="tel"
                        id="fraflytterTlf"
                        name="fraflytterTlf"
                        required
                        value={formData.fraflytterTlf}
                        onChange={handleChange}
                        placeholder="Tlf. nummer"
                        className="mt-1.5 w-full rounded-xl border border-slate-200 bg-slate-50/50 px-4 py-3 text-sm font-medium text-slate-900 focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:outline-none transition-all"
                      />
                    </div>
                    <div>
                      <label htmlFor="fraflytterEmail" className="block text-xs font-bold text-slate-700 uppercase tracking-wider">E-mail *</label>
                      <input
                        type="email"
                        id="fraflytterEmail"
                        name="fraflytterEmail"
                        required
                        value={formData.fraflytterEmail}
                        onChange={handleChange}
                        placeholder="E-mail adresse"
                        className="mt-1.5 w-full rounded-xl border border-slate-200 bg-slate-50/50 px-4 py-3 text-sm font-medium text-slate-900 focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:outline-none transition-all"
                      />
                    </div>
                  </div>
                </div>

                {/* Section 3: Tilflytter */}
                <div className="space-y-4">
                  <h3 className="text-lg font-bold text-slate-900 border-b border-slate-100 pb-2">3. Tilflytter (Køber / Lejer)</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="sm:col-span-2">
                      <label htmlFor="tilflytterNavn" className="block text-xs font-bold text-slate-700 uppercase tracking-wider">Navn *</label>
                      <input
                        type="text"
                        id="tilflytterNavn"
                        name="tilflytterNavn"
                        required
                        value={formData.tilflytterNavn}
                        onChange={handleChange}
                        placeholder="Tilflytterens fulde navn"
                        className="mt-1.5 w-full rounded-xl border border-slate-200 bg-slate-50/50 px-4 py-3 text-sm font-medium text-slate-900 focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:outline-none transition-all"
                      />
                    </div>
                    <div>
                      <label htmlFor="tilflytterTlf" className="block text-xs font-bold text-slate-700 uppercase tracking-wider">Telefon *</label>
                      <input
                        type="tel"
                        id="tilflytterTlf"
                        name="tilflytterTlf"
                        required
                        value={formData.tilflytterTlf}
                        onChange={handleChange}
                        placeholder="Tlf. nummer"
                        className="mt-1.5 w-full rounded-xl border border-slate-200 bg-slate-50/50 px-4 py-3 text-sm font-medium text-slate-900 focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:outline-none transition-all"
                      />
                    </div>
                    <div>
                      <label htmlFor="tilflytterEmail" className="block text-xs font-bold text-slate-700 uppercase tracking-wider">E-mail *</label>
                      <input
                        type="email"
                        id="tilflytterEmail"
                        name="tilflytterEmail"
                        required
                        value={formData.tilflytterEmail}
                        onChange={handleChange}
                        placeholder="E-mail adresse"
                        className="mt-1.5 w-full rounded-xl border border-slate-200 bg-slate-50/50 px-4 py-3 text-sm font-medium text-slate-900 focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:outline-none transition-all"
                      />
                    </div>
                  </div>
                </div>

                {/* Section 4: Overdragelse & Aflæsning */}
                <div className="space-y-4">
                  <h3 className="text-lg font-bold text-slate-900 border-b border-slate-100 pb-2">4. Overdragelse & Målerstand</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="overdragelsesDato" className="block text-xs font-bold text-slate-700 uppercase tracking-wider">Dato for overdragelse *</label>
                      <input
                        type="date"
                        id="overdragelsesDato"
                        name="overdragelsesDato"
                        required
                        value={formData.overdragelsesDato}
                        onChange={handleChange}
                        className="mt-1.5 w-full rounded-xl border border-slate-200 bg-slate-50/50 px-4 py-3 text-sm font-medium text-slate-900 focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:outline-none transition-all"
                      />
                    </div>
                    <div>
                      <label htmlFor="maelerstand" className="block text-xs font-bold text-slate-700 uppercase tracking-wider">Målerstand (hele m³) *</label>
                      <input
                        type="number"
                        id="maelerstand"
                        name="maelerstand"
                        required
                        value={formData.maelerstand}
                        onChange={handleChange}
                        placeholder="F.eks. 342"
                        className="mt-1.5 w-full rounded-xl border border-slate-200 bg-slate-50/50 px-4 py-3 text-sm font-medium text-slate-900 focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:outline-none transition-all"
                      />
                    </div>
                  </div>
                </div>

                {/* Remarks */}
                <div className="space-y-4">
                  <label htmlFor="bemaerkninger" className="block text-xs font-bold text-slate-700 uppercase tracking-wider">Bemærkninger / Besked</label>
                  <textarea
                    id="bemaerkninger"
                    name="bemaerkninger"
                    rows="4"
                    value={formData.bemaerkninger}
                    onChange={handleChange}
                    placeholder="Eventuelle bemærkninger til flytningen..."
                    className="mt-1.5 w-full rounded-xl border border-slate-200 bg-slate-50/50 px-4 py-3 text-sm font-medium text-slate-900 focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:outline-none transition-all"
                  />
                </div>

                {/* Error Message */}
                {status === 'error' && (
                  <div className="flex items-start gap-3 rounded-xl bg-rose-50 border border-rose-200 p-4 text-sm text-rose-800">
                    <AlertCircle className="h-5 w-5 shrink-0 text-rose-600" />
                    <span>{errorMessage}</span>
                  </div>
                )}

                {/* Submit Button */}
                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={status === 'loading'}
                    className="w-full flex items-center justify-center gap-2 rounded-full bg-blue-600 px-6 py-4 text-sm font-bold text-white shadow-lg shadow-blue-600/20 hover:bg-blue-500 hover:-translate-y-1 hover:shadow-blue-600/30 disabled:bg-blue-400 disabled:shadow-none transition-all duration-300 transform"
                  >
                    {status === 'loading' ? (
                      <>
                        <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                        Indsender meddelelse...
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4" />
                        Indsend Flyttemeddelelse
                      </>
                    )}
                  </button>
                </div>

              </form>
            )}
          </div>

          {/* Info Column */}
          <div className="lg:col-span-4 space-y-6">
            {/* Guide Card */}
            <div className="bg-white text-slate-950 border border-slate-200 rounded-3xl p-6 shadow-lg space-y-4">
              <h3 className="text-lg font-bold flex items-center gap-2 text-slate-950">
                <Info className="h-5 w-5 text-slate-950" />
                Vejledning
              </h3>
              <p className="text-xs text-slate-950 leading-relaxed">
                Når en ejendom skifter ejer eller lejer, skal måleren aflæses, og flytningen meddeles til vandværket.
              </p>
              <ul className="space-y-2.5 text-xs text-slate-950">
                <li className="flex items-start gap-2">
                  <span className="font-bold text-slate-950">1.</span>
                  <span>Aflæs vandmåleren på selve overdragelsesdagen.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-bold text-slate-950">2.</span>
                  <span>Udfyld alle felter markeret med stjerne (*).</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-bold text-slate-950">3.</span>
                  <span>Både fraflytter og tilflytter skal godkende tallene.</span>
                </li>
              </ul>
              <div className="border-t border-slate-100 pt-4 text-xs text-slate-500">
                Har du spørgsmål? Ring til kontoret på tlf. <a href="tel:61286529" className="text-slate-950 hover:text-slate-700 hover:underline font-semibold">61 28 65 29</a>.
              </div>
            </div>

            {/* Image Card */}
            <div className="rounded-3xl overflow-hidden border border-slate-200 shadow-sm bg-white">
              <img
                src={IMAGES.movingHero}
                alt="Vandmåler og flytning"
                className="w-full h-48 object-cover"
              />
              <div className="p-5 space-y-2">
                <h4 className="font-bold text-slate-900 text-sm">Hvor sidder måleren?</h4>
                <p className="text-xs text-slate-500 leading-relaxed">
                  Vandmåleren sidder typisk i et bryggers, en kælder eller i en målerbrønd udenfor. Aflæs kun de hele kubikmeter (m³).
                </p>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
